<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4 mt-5">
      <div class="d-flex align-items-center">
        <h2>Insync Logins</h2>
        <button @click="addLogin" class="btn btn-success ml-4">
          Add Login
        </button>
      </div>
      <div style="width: 50%;">
        <SearchBox
          v-model="searchTerm"
          placeholder="Search logins..."
        />
      </div>
    </div>
    <AIVTable
      v-if="filteredLogins && filteredLogins.length"
      :columns="loginColumns"
      :data="filteredLogins"
      :pagination="{ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }"
    >
      <template #cell-password="">
        <span class="password-hidden">••••••••</span>
      </template>
      <template #cell-site_url="{ row }">
        <div class="site-url-column">
          <div v-if="row.siteURL && row.siteURL !== 'https://midwest.insynchcs.com/'" class="custom-url">
            <i class="fas fa-exclamation-triangle text-warning mr-1" title="Custom URL - may cause reporting errors"></i>
            <a :href="row.siteURL" target="_blank" class="text-primary">
              {{ row.siteURL }}
            </a>
          </div>
          <div v-else class="default-url text-muted">
            https://midwest.insynchcs.com/
          </div>
        </div>
      </template>
      <template #cell-drive_location="{ row }">
        <div class="drive-location-column">
          <div v-if="row.driveLocation && row.driveLocation !== 'empty'" class="custom-drive">
            <a :href="row.driveLocation" target="_blank" class="text-primary">
              {{ row.driveLocation }}
            </a>
          </div>
          <div v-else-if="row.driveLocation === 'empty'" class="empty-drive text-warning">
            <i class="fas fa-exclamation-triangle mr-1" title="Drive location not configured"></i>
            Drive location not configured
          </div>
          <div v-else class="default-drive text-muted">
            Default Drive Location
          </div>
        </div>
      </template>
      <template #cell-dates="{ row }">
        <div class="dates-column">
          <div class="created-date">Created: {{ formatDate(row.createdAt) }}</div>
          <div class="updated-date">Updated: {{ formatDate(row.updatedAt) }}</div>
        </div>
      </template>
      <template #cell-actions="{ row }">
        <button @click="editLogin(row)" class="btn btn-primary btn-sm mr-2">
          Edit
        </button>
        <button @click="changePassword(row)" class="btn btn-warning btn-sm mr-2">
          Change Password
        </button>
        <button @click="removeLogin(row)" class="btn btn-danger btn-sm">
          Delete
        </button>
      </template>
    </AIVTable>

    <!-- Login Modal -->
    <AIVDialog
      :visible="$modal.state.loginModal"
      :title="modalTitle"
      :show-footer="true"
      :custom-class="'login-modal'"
      @update:visible="$modal.close('loginModal')"
    >
      <!-- Edit/Create Mode -->
      <div v-if="modal.mode === 'edit' || modal.mode === 'create'" class="login-form">
        <div class="form-group mb-3">
          <label for="pracId" class="form-label">Blue Bar ID *</label>
          <input
            id="pracId"
            v-model="modal.formData.pracId"
            type="number"
            class="form-control"
            placeholder="Enter Blue Bar ID"
            required
          />
        </div>
        <div class="form-group mb-3">
          <label for="loginName" class="form-label">Name *</label>
          <input
            id="loginName"
            v-model="modal.formData.name"
            type="text"
            class="form-control"
            placeholder="Enter name"
            required
          />
        </div>
        <div class="form-group mb-3">
          <label for="facilityName" class="form-label">Facility Name</label>
          <input
            id="facilityName"
            v-model="modal.formData.facilityName"
            type="text"
            class="form-control"
            placeholder="Enter facility name (optional)"
          />
        </div>
        <div class="form-group mb-3">
          <label for="username" class="form-label">Username *</label>
          <input
            id="username"
            v-model="modal.formData.username"
            type="text"
            class="form-control"
            placeholder="Enter username"
            required
          />
        </div>
        
        <!-- Password field - only for create mode -->
        <div v-if="modal.mode === 'create'" class="form-group mb-3">
          <label for="password" class="form-label">Password *</label>
          <input
            id="password"
            v-model="modal.formData.password"
            type="password"
            class="form-control"
            placeholder="Enter password"
            required
          />
        </div>
        
        <!-- Insync URL Configuration -->
        <div class="form-group mb-3">
          <div class="form-check mb-2">
            <input
              id="useDefaultURL"
              v-model="modal.formData.useDefaultURL"
              type="checkbox"
              class="check-input"
            />
            <label for="useDefaultURL" class="form-check-label">
              Use Default Insync URL
            </label>
          </div>
          <div v-if="!modal.formData.useDefaultURL" class="mt-2">
            <label for="customURL" class="form-label">Custom Insync URL</label>
            <input
              id="customURL"
              v-model="modal.formData.siteURL"
              type="text"
              class="form-control"
              placeholder="Enter custom Insync URL"
            />
            <div class="alert alert-warning mt-2 mb-0" role="alert">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              <small>Adding a custom URL may cause reporting errors.</small>
            </div>
          </div>
        </div>
        
        <!-- Google Drive Configuration -->
        <div class="form-group mb-3">
          <div class="form-check mb-2">
            <input
              id="useDefaultDrive"
              v-model="modal.formData.useDefaultDrive"
              type="checkbox"
              class="check-input"
            />
            <label for="useDefaultDrive" class="form-check-label">
              Use Default Drive Location
            </label>
          </div>
          <div v-if="!modal.formData.useDefaultDrive" class="mt-2">
            <label for="customDrive" class="form-label">Custom Drive Location URL</label>
            <input
              id="customDrive"
              v-model="modal.formData.driveLocation"
              type="text"
              class="form-control"
              placeholder="Enter custom Google Drive folder URL"
            />
          </div>
        </div>
      </div>

      <!-- Remove Mode -->
      <div v-if="modal.mode === 'remove'" class="login-remove-confirm">
        <p class="text-center mb-4">
          Are you sure you want to delete the login 
          <strong>"{{ modal.selectedLogin?.username }}"</strong>?
        </p>
        <p class="text-muted text-center">This action cannot be undone.</p>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <div class="d-flex justify-content-around">
          <button 
            @click="closeModal" 
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            v-if="modal.mode === 'edit'"
            @click="saveLogin" 
            class="btn btn-primary ml-2"
            :disabled="modal.loading || !modal.formData.pracId || !modal.formData.name || !modal.formData.username || (modal.mode === 'create' && !modal.formData.password)"
          >
            {{ modal.loading ? 'Saving...' : 'Save' }}
          </button>
          <button 
            v-if="modal.mode === 'create'"
            @click="createLogin" 
            class="btn btn-success ml-2"
            :disabled="modal.loading || !modal.formData.pracId || !modal.formData.name || !modal.formData.username || (modal.mode === 'create' && !modal.formData.password)"
          >
            {{ modal.loading ? 'Creating...' : 'Create Login' }}
          </button>
          <button 
            v-if="modal.mode === 'remove'"
            @click="confirmRemoval" 
            class="btn btn-danger ml-2"
            :disabled="modal.loading"
          >
            {{ modal.loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </template>
    </AIVDialog>

    <!-- Password Change Modal -->
    <AIVDialog
      :visible="$modal.state.passwordModal"
      title="Change Password"
      :show-footer="true"
      :custom-class="'password-modal'"
      @update:visible="$modal.close('passwordModal')"
    >
      <div class="password-form">
        <div class="form-group mb-3">
          <label for="newPassword" class="form-label">New Password *</label>
          <div class="password-input-wrapper">
            <input
              id="newPassword"
              v-model="passwordModal.formData.newPassword"
              :type="passwordModal.showNewPassword ? 'text' : 'password'"
              class="form-control"
              placeholder="Enter new password"
              required
            />
            <button
              type="button"
              class="password-toggle-btn"
              @click="passwordModal.showNewPassword = !passwordModal.showNewPassword"
            >
              <i :class="passwordModal.showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>
        
        <div class="form-group mb-3">
          <label for="confirmPassword" class="form-label">Confirm Password *</label>
          <div class="password-input-wrapper">
            <input
              id="confirmPassword"
              v-model="passwordModal.formData.confirmPassword"
              :type="passwordModal.showConfirmPassword ? 'text' : 'password'"
              class="form-control"
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              class="password-toggle-btn"
              @click="passwordModal.showConfirmPassword = !passwordModal.showConfirmPassword"
            >
              <i :class="passwordModal.showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>

        <div v-if="passwordModal.error" class="alert alert-danger" role="alert">
          {{ passwordModal.error }}
        </div>
      </div>

      <template #footer>
        <div class="d-flex justify-content-around">
          <button 
            @click="closePasswordModal" 
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            @click="saveNewPassword" 
            class="btn btn-primary ml-2"
            :disabled="passwordModal.loading || !passwordModal.formData.newPassword || !passwordModal.formData.confirmPassword"
          >
            {{ passwordModal.loading ? 'Saving...' : 'Save New Password' }}
          </button>
        </div>
      </template>
    </AIVDialog>

  </div>
</template>

<script>
import InsyncAPIService from "@/serviceHandlers/InsyncAPIService";
import AIVTable from "@/components/AIVTable.vue";
import AIVDialog from "@/components/AIVDialog.vue";
import SearchBox from "@/components/SearchBox.vue";
import { encryptPassword, isEncrypted } from "@/utils/cryptoUtils";

const insyncAPIService = new InsyncAPIService()

export default {
  name: "InsyncPage",
  components: {
    AIVTable,
    AIVDialog,
    SearchBox
  },
  data(){
    return {
      searchTerm: '',
      logins: [],
      loginColumns: [
        { key: 'pracId', label: 'Blue Bar ID' },
        { key: 'name', label: 'Name' },
        { key: 'facilityName', label: 'Facility Name' },
        { key: 'username', label: 'Username' },
        { key: 'password', label: 'Password' },
        { key: 'site_url', label: 'Site URL', component: 'site_url' },
        { key: 'drive_location', label: 'Drive Location', component: 'drive_location' },
        { key: 'dates', label: 'Created / Updated' },
        { 
          key: 'actions', 
          label: 'Actions', 
          sortable: false
        }
      ],
      modal: {
        mode: null, // 'edit', 'remove', or 'create'
        selectedLogin: null,
        loading: false,
        formData: {
          pracId: '',
          name: '',
          facilityName: '',
          username: '',
          password: '',
          useDefaultURL: true,
          siteURL: '',
          useDefaultDrive: true,
          driveLocation: ''
        }
      },
      passwordModal: {
        selectedLogin: null,
        loading: false,
        error: null,
        showNewPassword: false,
        showConfirmPassword: false,
        formData: {
          newPassword: '',
          confirmPassword: ''
        }
      }
    }
  },
  computed: {
    filteredLogins() {
      if (!this.logins || !this.searchTerm) {
        return this.logins || [];
      }
      
      const searchLower = this.searchTerm.toLowerCase();
      return this.logins.filter(login => {
        const matchesName = login.name?.toLowerCase().includes(searchLower);
        const matchesUsername = login.username?.toLowerCase().includes(searchLower);
        const matchesFacilityName = login.facilityName?.toLowerCase().includes(searchLower);
        
        return matchesName || matchesUsername || matchesFacilityName;
      });
    },
    modalTitle() {
      if (this.modal.mode === 'edit') {
        return 'Edit Login';
      } else if (this.modal.mode === 'remove') {
        return 'Delete Login';
      } else if (this.modal.mode === 'create') {
        return 'Create New Login';
      }
      return '';
    }
  },
  async mounted(){
    console.clear()
    console.log(process.env.VUE_APP_ENCRYPTION_KEY)
    await this.getLogins()
  },
  methods: {
    async getLogins(){
      try {
        this.logins = await insyncAPIService.getInsyncLogins(this.$router)
        console.log(this.logins)
      } catch (e) {
        console.error(e)
      }
    },
    editLogin(login) {
      console.log(login)
      this.modal.selectedLogin = login;
      this.modal.mode = 'edit';
      this.modal.formData.pracId = login.pracId;
      this.modal.formData.name = login.name;
      this.modal.formData.facilityName = login.facilityName || '';
      this.modal.formData.username = login.username;
      this.modal.formData.useDefaultURL = !login.siteURL || login.siteURL === 'https://midwest.insynchcs.com/';
      this.modal.formData.siteURL = login.siteURL || '';
      this.modal.formData.useDefaultDrive = !login.driveLocation;
      this.modal.formData.driveLocation = login.driveLocation || '';
      this.$modal.open('loginModal');
    },
    removeLogin(login) {
      this.modal.selectedLogin = login;
      this.modal.mode = 'remove';
      this.$modal.open('loginModal');
    },
    addLogin() {
      this.modal.mode = 'create';
      this.modal.selectedLogin = null;
      this.modal.formData.pracId = '';
      this.modal.formData.name = '';
      this.modal.formData.facilityName = '';
      this.modal.formData.username = '';
      this.modal.formData.password = '';
      this.modal.formData.useDefaultURL = true;
      this.modal.formData.siteURL = '';
      this.modal.formData.useDefaultDrive = true;
      this.modal.formData.driveLocation = '';
      this.$modal.open('loginModal');
    },
    closeModal() {
      this.$modal.close('loginModal');
      this.modal.mode = null;
      this.modal.selectedLogin = null;
      this.modal.loading = false;
      this.modal.formData.pracId = '';
      this.modal.formData.name = '';
      this.modal.formData.facilityName = '';
      this.modal.formData.username = '';
      this.modal.formData.password = '';
      this.modal.formData.useDefaultURL = true;
      this.modal.formData.siteURL = '';
      this.modal.formData.useDefaultDrive = true;
      this.modal.formData.driveLocation = '';
    },
    async saveLogin() {
      this.modal.loading = true;
      try {
        const loginData = {
          pracId: this.modal.formData.pracId,
          name: this.modal.formData.name,
          facilityName: this.modal.formData.facilityName || null,
          username: this.modal.formData.username,
          siteURL: this.modal.formData.useDefaultURL ? 'https://midwest.insynchcs.com/' : this.modal.formData.siteURL,
          driveLocation: this.modal.formData.useDefaultDrive ? null : this.modal.formData.driveLocation
        };

        console.log(loginData)
        
        await insyncAPIService.updateInsyncLogin(
          this.modal.selectedLogin.id,
          loginData,
          this.$router
        );
        
        // Refresh the logins data to show updated info
        await this.getLogins();
        this.closeModal();
        
        console.log('Login updated successfully');
      } catch (error) {
        console.error('Error updating login:', error);
        alert('Failed to update login. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async createLogin() {
      this.modal.loading = true;
      try {
        // Encrypt password before sending to API
        const encryptedPassword = this.modal.formData.password ? encryptPassword(this.modal.formData.password) : '';
        
        const loginData = {
          pracId: this.modal.formData.pracId,
          name: this.modal.formData.name,
          facilityName: this.modal.formData.facilityName || null,
          username: this.modal.formData.username,
          password: encryptedPassword,
          siteURL: this.modal.formData.useDefaultURL ? 'https://midwest.insynchcs.com/' : this.modal.formData.siteURL,
          driveLocation: this.modal.formData.useDefaultDrive ? null : this.modal.formData.driveLocation
        };
        
        await insyncAPIService.createInsyncLogin(
          loginData,
          this.$router
        );
        
        // Refresh the logins data to show the new login
        await this.getLogins();
        this.closeModal();
        
        console.log('Login created successfully');
      } catch (error) {
        console.error('Error creating login:', error);
        alert('Failed to create login. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async confirmRemoval() {
      this.modal.loading = true;
      try {
        await insyncAPIService.deleteInsyncLogin(
          this.modal.selectedLogin.id,
          this.$router
        );
        
        // Refresh the logins data to remove the login from the list
        await this.getLogins();
        this.closeModal();
        
        console.log('Login deleted successfully');
      } catch (error) {
        console.error('Error deleting login:', error);
        alert('Failed to delete login. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    changePassword(login) {
      this.passwordModal.selectedLogin = login;
      this.passwordModal.formData.newPassword = '';
      this.passwordModal.formData.confirmPassword = '';
      this.passwordModal.showNewPassword = false;
      this.passwordModal.showConfirmPassword = false;
      this.passwordModal.error = null;
      this.passwordModal.loading = false;
      this.$modal.open('passwordModal');
    },
    closePasswordModal() {
      this.$modal.close('passwordModal');
      this.passwordModal.selectedLogin = null;
      this.passwordModal.formData.newPassword = '';
      this.passwordModal.formData.confirmPassword = '';
      this.passwordModal.showNewPassword = false;
      this.passwordModal.showConfirmPassword = false;
      this.passwordModal.error = null;
      this.passwordModal.loading = false;
    },
    async saveNewPassword() {
      // Validate passwords match
      if (this.passwordModal.formData.newPassword !== this.passwordModal.formData.confirmPassword) {
        this.passwordModal.error = 'Passwords do not match.';
        return;
      }

      // Validate password length
      if (this.passwordModal.formData.newPassword.length < 6) {
        this.passwordModal.error = 'Password must be at least 6 characters long.';
        return;
      }

      this.passwordModal.loading = true;
      this.passwordModal.error = null;

      try {
        const encryptedPassword = encryptPassword(this.passwordModal.formData.newPassword);
        
        const passwordData = {
          password: encryptedPassword
        };

        await insyncAPIService.updateInsyncLogin(
          this.passwordModal.selectedLogin.id,
          passwordData,
          this.$router
        );
        
        // Refresh the logins data to show updated info
        await this.getLogins();
        this.closePasswordModal();
        
        console.log('Password updated successfully');
        alert('Password changed successfully!');
      } catch (error) {
        console.error('Error updating password:', error);
        this.passwordModal.error = 'Failed to update password. Please try again.';
      } finally {
        this.passwordModal.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.dates-column {
  font-size: 0.875rem;
}

.created-date {
  margin-bottom: 2px;
}

.updated-date {
  color: #6c757d;
  font-size: 0.8rem;
}

.password-hidden {
  font-family: monospace;
  letter-spacing: 2px;
}

.site-url-column {
  font-size: 0.875rem;
}

.custom-url a {
  text-decoration: none;
  word-break: break-all;
}

.custom-url a:hover {
  text-decoration: underline;
}

.default-url {
  font-style: italic;
}

.check-input {
  opacity: 1 !important;
  position: relative !important;
  visibility: visible !important;
  background-color: white;
  border-color: #000;
}

.check-input:checked {
  background-color: #000;
  border-color: #000;
}

.check-input:focus {
  border-color: #000;
  box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.25);
}

.password-input-wrapper {
  position: relative;
}

.password-toggle-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  z-index: 2;
}

.password-toggle-btn:hover {
  color: #495057;
}

.password-toggle-btn:focus {
  outline: none;
  color: #495057;
}

.drive-location-column {
  font-size: 0.875rem;
}

.custom-drive a {
  text-decoration: none;
  word-break: break-all;
}

.custom-drive a:hover {
  text-decoration: underline;
}

.default-drive {
  font-style: italic;
}

</style>
